import { ApolloClient, ApolloLink, createHttpLink } from '@apollo/client'
import { InMemoryCache } from '@apollo/client/cache'
import fetch from 'isomorphic-unfetch'
import { setContext } from 'apollo-link-context'
import { environments } from 'utils/Environments'
import { SentryLink } from 'apollo-link-sentry'

const API_PATH = '/api/graphql'

function getServerGqlEndpoint(environment) {
  if (process.browser) {
    return API_PATH
  }

  if (environment === environments.DEVELOPMENT) {
    return `${process.env.CGC_WEB_CGC_APP_URL}${API_PATH}`
  }

  return `http://cgc-app:3000${API_PATH}`
}

function getHttpLink(environment, domain, host, cookies, cloudflareForwardedIp) {
  const isServerSide = !process.browser
  const uri = getServerGqlEndpoint(environment)
  const httpLink = createHttpLink({
    uri: uri,
    credentials: 'same-origin',
    fetch: isServerSide && fetch,
  })

  if (!isServerSide && environment !== environments.STAGING) {
    return httpLink
  }

  const headersLink = setContext((_, { headers }) => {
    headers = {
      ...headers,
      'x-cgc-domain': domain,
      'x-cgc-host': host,
    }

    if (cloudflareForwardedIp) {
      headers = {
        ...headers,
        'x-cgc-forwarded-ip': cloudflareForwardedIp,
      }
    }

    if (isServerSide) {
      headers = getHeadersWithAuhenticatedCookies(headers, cookies)
    }

    if (environment === environments.STAGING) {
      headers = getHeaderswithBaiscAuth(headers)
    }

    return { headers: headers }
  })
  return headersLink.concat(httpLink)
}

const getHeaderswithBaiscAuth = (headers) => {
  const btoa = require('btoa')

  return {
    ...headers,
    authorization: 'Basic ' + btoa('careguidebeta:testdemo'),
  }
}

const getHeadersWithAuhenticatedCookies = (headers, cookies) => {
  if (!cookies) {
    return headers
  }

  return {
    ...headers,
    Cookie: cookies,
  }
}

let apolloClient = null
function create(initialState, environment, domain, host, cookies, cloudflareForwardedIp) {
  const httpLink = getHttpLink(environment, domain, host, cookies, cloudflareForwardedIp)
  const sentryLinkOptions = {
    breadcrumb: {
      includeQuery: true,
      includeVariables: false,
      includeResponse: false,
      includeError: true,
    },
  }
  return new ApolloClient({
    connectToDevTools: process.browser,
    ssrMode: !process.browser,
    link: ApolloLink.from([new SentryLink(sentryLinkOptions), httpLink]),
    cache: new InMemoryCache().restore(initialState || {}),
  })
}

export default function initApollo(
  initialState,
  environment,
  domain,
  host,
  cookies = null,
  cloudflareForwardedIp
) {
  if (!process.browser) {
    return create(initialState, environment, domain, host, cookies, cloudflareForwardedIp)
  }

  if (!apolloClient) {
    apolloClient = create(initialState, environment, domain, host, cookies, cloudflareForwardedIp)
  }

  return apolloClient
}
